/**
 * SearchResultsPage — full learner search results with nav + back affordance.
 *
 * Uses scenario_os_token (not the legacy `token` key) and loads domain
 * filters from /curriculum/search/domains so only enrolled/accessible
 * domains appear.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LearnerNav from '../../components/nav/LearnerNav';

function getToken() {
  return localStorage.getItem('scenario_os_token')
    || localStorage.getItem('token')
    || '';
}

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get('q') || '';
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [difficulty, setDifficulty] = useState(params.get('difficulty') || '');
  const [contentType, setContentType] = useState(params.get('type') || '');
  const [domainOptions, setDomainOptions] = useState([]);

  useEffect(() => {
    const token = getToken();
    fetch('/scenario-os/curriculum/search/domains', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : { domains: [] }))
      .then((data) => setDomainOptions(data.domains || []))
      .catch(() => setDomainOptions([]));
  }, []);

  const fetchResults = useCallback(async () => {
    if (!query) {
      setResults([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const p = new URLSearchParams({ q: query, limit: '20' });
      if (domain) p.set('domain', domain);
      if (difficulty) p.set('difficulty', difficulty);
      if (contentType) p.set('content_type', contentType);
      const token = getToken();
      const res = await fetch(`/scenario-os/curriculum/search?${p}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setResults([]);
        setTotal(0);
        return;
      }
      const data = await res.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, domain, difficulty, contentType]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const handleResultClick = (r) => {
    if (r.type === 'tutorial') {
      navigate(`/tutorials/${r.domain || 'ai'}/play/${r.id}`);
      return;
    }
    // Scene / segment — prefer tutorial player when we have tutorial_id
    if (r.tutorial_id) {
      navigate(`/tutorials/${r.domain || 'ai'}/play/${r.tutorial_id}`);
      return;
    }
    navigate(`/session/${r.domain || 'ai'}/${r.id}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0' }}>
      <LearnerNav />
      <div style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate('/dashboard');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 16,
            padding: '7px 14px',
            border: '1px solid #FFCF99',
            borderRadius: 8,
            background: '#FFF8F0',
            color: '#92140C',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>

        <h2 style={{ margin: '0 0 4px', color: '#1E1E24', fontSize: 24, fontWeight: 700 }}>
          Search Results
        </h2>
        <p style={{ color: '#6a6a72', marginBottom: 16, fontSize: '0.875rem' }}>
          {loading ? 'Searching…' : `${total} result${total !== 1 ? 's' : ''} for “${query}”`}
        </p>

        {/* Filters — enrolled domains only */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            aria-label="Filter by domain"
            style={selectStyle}
          >
            <option value="">All Domains</option>
            {domainOptions.map((d) => (
              <option key={d.domain_id} value={d.domain_id}>
                {d.display_name || d.domain_id}
              </option>
            ))}
          </select>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            aria-label="Filter by level"
            style={selectStyle}
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            aria-label="Filter by content type"
            style={selectStyle}
          >
            <option value="">All Types</option>
            <option value="tutorial">Tutorials</option>
            <option value="scene">Scenes</option>
          </select>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6a6a72' }}>Searching…</div>
        ) : results.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6a6a72' }}>
            No results found. Try different search terms.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {results.map((r, i) => (
              <div
                key={`${r.type}-${r.id}-${i}`}
                onClick={() => handleResultClick(r)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResultClick(r); }}
                role="button"
                tabIndex={0}
                style={{
                  padding: 16,
                  borderRadius: 10,
                  border: '1px solid #FFCF99',
                  background: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(30,30,36,0.06)',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 4, color: '#1E1E24' }}>
                  {r.title}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6a6a72', lineHeight: 1.5 }}>
                  {(r.description || '').substring(0, 200)}
                  {(r.description || '').length > 200 ? '…' : ''}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={chip(r.type === 'tutorial' ? '#dbeafe' : '#f3e8ff', r.type === 'tutorial' ? '#1e40af' : '#7c3aed')}>
                    {r.scene_type || r.type}
                  </span>
                  {r.domain && (
                    <span style={chip('#f0fdf4', '#166534')}>{r.domain}</span>
                  )}
                  {r.difficulty && (
                    <span style={chip('#fefce8', '#854d0e')}>{r.difficulty}</span>
                  )}
                  {r.estimated_minutes && (
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      ~{r.estimated_minutes} min
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const selectStyle = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #FFCF99',
  fontSize: '0.85rem',
  background: '#fff',
  color: '#1E1E24',
};

function chip(bg, color) {
  return {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.7rem',
    background: bg,
    color,
    fontWeight: 600,
    textTransform: 'capitalize',
  };
}
